import { ref, computed, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import _ from 'lodash'
import StreamService from '@/services/StreamService'
import { showModal } from '@/Utils/modalBus'
import { track, EVENTS } from '@/lib/tracking'
import { pushStreamFailOverEnabled } from '@/Utils/platformStatus'

export function usePlatformToggle({
	stream,
	mediaPulse,
	streamPlatforms,
	groups,
	groupToggleStateMap,
	groupToggleProcessing,
	schedulerConfig,
	computeGroupToggleStateForGroup,
	onPlatformList,
}) {
	const store = useStore()
	const { notify } = useNotification()
	const pendingToggleCount = ref(0)
	// Read by the platform list to hold off refreshes that would overwrite optimistic state.
	const platformToggleProcessing = computed(() => pendingToggleCount.value > 0)
	const groupToggleState = ref(false)
	const focusedPlatform = ref({})
	const appNotificationMsg = ref('')

	// Per destination: the state the API last confirmed and the state the user wants
	// now. One request per destination at a time; clicks made while it is in flight
	// collapse into a single follow-up request for the latest state.
	const toggleQueue = new Map()

	// The API checks an enable against the destination limit using the states it has
	// already saved, so enables sent together could each pass it. They go one at a time.
	let enableLane = Promise.resolve()
	function inEnableLane(task) {
		const run = enableLane.then(task)
		enableLane = run.catch(() => {})
		return run
	}

	function computeGroupToggleState() {
		const platforms = streamPlatforms.value
		const platformCount = _.size(platforms)
		const enabledPlatformCount = _.size(_.filter(platforms, (p) => p.enabled))
		groupToggleState.value = enabledPlatformCount === platformCount
	}

	function syncGroupToggleState(platform) {
		const group = groups.value.find((g) => g.selected.includes(platform._id))
		if (group) computeGroupToggleStateForGroup(group.name)
	}

	function setLocalEnabled(platform, enabled) {
		platform.enabled = enabled
		const current = streamPlatforms.value.find((p) => p._id === platform._id)
		if (current && current !== platform) current.enabled = enabled
	}

	function canTogglePlatform(platform, enabled) {
		if (platform.template === 'facebook live' && !platform.isTokenValid) return false
		if (pushStreamFailOverEnabled(platform)) return false
		const facebookInLoop = schedulerConfig.value?.mode === 'loop'
			&& platform.template === 'facebook live'
			&& platform.linkedServiceCreds
		if (facebookInLoop && enabled) return false
		return true
	}

	async function putPlatformState(platform, entry) {
		const enabled = entry.desired
		if (enabled === entry.confirmed) return enabled
		await StreamService.toggleStreamPlatform(stream.value._id, platform._id, enabled)
		return enabled
	}

	async function sendPlatformState(platform, entry) {
		pendingToggleCount.value++
		try {
			while (entry.desired !== entry.confirmed) {
				let saved
				try {
					saved = entry.desired
						? await inEnableLane(() => putPlatformState(platform, entry))
						: await putPlatformState(platform, entry)
				} catch (err) {
					entry.desired = entry.confirmed
					setLocalEnabled(platform, entry.confirmed)
					notify({
						group: 'error',
						title: "Couldn't toggle platform status",
						text: err.message,
					})
					if (err && err.code === 403 && err.message) {
						appNotificationMsg.value = err.message
						showModal('modal-trial-publish-limit')
					}
					return false
				}
				if (saved === entry.confirmed) continue
				entry.confirmed = saved
				store.dispatch('Streams/patchStreamPlatformEnabled', {
					streamId: stream.value._id,
					platformId: platform._id,
					enabled: saved,
				})
				track(EVENTS.DESTINATION_TOGGLED, { enabled: saved })
			}
			return true
		} finally {
			toggleQueue.delete(platform._id)
			pendingToggleCount.value--
			onPlatformList(streamPlatforms.value)
			computeGroupToggleState()
			syncGroupToggleState(platform)
		}
	}

	/**
	 * Show `enabled` on the switch now and save it in the background.
	 * `previous` is the state the API holds when no request is already queued.
	 */
	function requestPlatformState(platform, enabled, previous) {
		setLocalEnabled(platform, enabled)
		const queued = toggleQueue.get(platform._id)
		if (queued) {
			queued.desired = enabled
			return queued.running
		}
		const entry = { confirmed: previous, desired: enabled, running: null }
		toggleQueue.set(platform._id, entry)
		entry.running = sendPlatformState(platform, entry)
		return entry.running
	}

	function setPlatformsEnabled(platforms, enabled) {
		const targets = _.filter(platforms, (p) => p && canTogglePlatform(p, enabled))
		return Promise.all(targets.map((p) => requestPlatformState(p, enabled, !!p.enabled)))
	}

	async function toggleGroupStatus(state = groupToggleState.value) {
		groupToggleProcessing.value = true
		for (const group of groups.value) {
			groupToggleStateMap[group.name] = state
		}
		try {
			await setPlatformsEnabled(streamPlatforms.value, state)
		} finally {
			groupToggleProcessing.value = false
			computeGroupToggleState()
		}
	}

	function onToggleOffWarningConfirmed() {
		return togglePlatformStatus(focusedPlatform.value, false)
	}

	/** Switch `@change` handler: `v-model` has already set `platform.enabled` to the new state. */
	function onTogglePlatformStatus(platform) {
		const enabled = !!platform.enabled
		const previous = !enabled
		if (!enabled && platform.template === 'facebook live' && mediaPulse.value?.alive) {
			focusedPlatform.value = platform
			nextTick(() => setLocalEnabled(platform, true))
			return showModal('toggle-off-warning')
		}
		if (!canTogglePlatform(platform, enabled)) {
			setLocalEnabled(platform, previous)
			return Promise.resolve(false)
		}
		return requestPlatformState(platform, enabled, previous)
	}

	/** Set a destination to `forceState`, or flip it, from code rather than its switch. */
	function togglePlatformStatus(platform, forceState) {
		const previous = !!platform.enabled
		const enabled = forceState !== undefined ? !!forceState : !previous
		if (!canTogglePlatform(platform, enabled)) return Promise.resolve(false)
		return requestPlatformState(platform, enabled, previous)
	}

	return {
		platformToggleProcessing,
		groupToggleState,
		focusedPlatform,
		appNotificationMsg,
		computeGroupToggleState,
		toggleGroupStatus,
		onToggleOffWarningConfirmed,
		onTogglePlatformStatus,
		togglePlatformStatus,
		setPlatformsEnabled,
	}
}
